import React from "react"
import styled from "styled-components"
import media from "styles/media"
import colors from "styles/colors"
import text from "styles/text"
import Checkmark from "images/Checkmark.svg"

const GuidedContent = ({ content }) => {
  const sectionsData = content?.section?.map((item, index) => {
    return (
      <Section
        $numbers={content?.variation === "Numbers"}
        $checkmark={content?.variation === "Checkmark"}
        key={content.sectionId + index}
      >
        {item?.icon && (
          <Icon src={item?.icon?.sourceUrl} alt={item?.icon?.altText} />
        )}
        {content?.variation === "Numbers" && (
          <NumberedDiv>{index + 1}</NumberedDiv>
        )}
        {content?.variation === "Checkmark" && (
          <CheckmarkIcon src={Checkmark} alt={"checkmark-svg"} />
        )}
        <TextWrapper>
          {item?.headline && <Headline>{item?.headline}</Headline>}
          {item?.body && (
            <BodyCopy dangerouslySetInnerHTML={{ __html: item?.body }} />
          )}
        </TextWrapper>
      </Section>
    )
  })

  return (
    <Wrapper $lineseperator={content?.lineSeperator}>
      {content?.header && (
        <HeaderDiv>
          {content?.sectionId && <SectionId> {content?.sectionId} </SectionId>}
          {content?.header && (
            <Header
              $color={content?.headerColor}
              dangerouslySetInnerHTML={{ __html: content?.header }}
            />
          )}
        </HeaderDiv>
      )}
      {content?.introBody && (
        <IntroBody dangerouslySetInnerHTML={{ __html: content.introBody }} />
      )}
      <ContentDiv>{content?.section && sectionsData}</ContentDiv>
      {content?.bottomCopy && (
        <BottomCopy dangerouslySetInnerHTML={{ __html: content?.bottomCopy }} />
      )}
    </Wrapper>
  )
}

export default GuidedContent

const Headline = styled.h5`
  ${text.h5}

  ${media.mobile} {
    ${text.h4};
  }
`
const NumberedDiv = styled.div`
  display: flex;
  text-align: center;
  ${text.bodyMdBold};
  color: ${colors.primaryOrange};
  border: 0.278vw solid ${colors.primaryOrange};
  border-radius: 100%;
  padding: 0.139vw 0.625vw;

  ${media.fullWidth} {
    border: 4px solid ${colors.primaryOrange};
    padding: 2px 9px;
  }

  ${media.tablet} {
    border: 0.391vw solid ${colors.primaryOrange};
    padding: 0.195vw 0.879vw;
  }

  ${media.mobile} {
    border: 0.935vw solid ${colors.primaryOrange};
    padding: 0.467vw 2.103vw;
  }
`
const Section = styled.div`
  display: flex;
  flex-direction: row;
  align-items: ${props => (props.$checkmark ? "flex-start" : "center")};
  gap: ${props => (props.$checkmark || props.$numbers ? "1.389vw" : "2.778vw")};
  padding: 20px 0px;

  ${media.fullWidth} {
    padding: 20px 0px;
    gap: ${props => (props.$checkmark || props.$numbers ? "20px" : "40px")};
  }

  ${media.tablet} {
    padding: 1.953vw 0vw;
    gap: ${props =>
      props.$checkmark || props.$numbers ? "1.953vw" : "3.906vw"};
  }

  ${media.mobile} {
    padding: 4.673vw 0vw;
    gap: ${props =>
      props.$checkmark || props.$numbers ? "4.673vw" : "9.346vw"};
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.389vw;

  ${media.fullWidth} {
    gap: 20px;
  }

  ${media.tablet} {
    gap: 1.953vw;
  }

  ${media.mobile} {
    gap: 4.673vw;
  }
`

const IntroBody = styled.p`
  ${text.bodySm}
  padding:1.389vw 0vw;

  h1 {
    ${text.h1};
  }
  h2 {
    ${text.h3};
  }
  h3 {
    ${text.h3};
    margin-top: 3.505vw;

    ${media.fullWidth} {
      margin-top: 15px;
    }

    ${media.tablet} {
      margin-top: 1.465vw;
    }

    ${media.mobile} {
      margin-top: 3.505vw;
    }
  }
  h4 {
    ${text.h3};
  }
  h5 {
    ${text.h5};
    margin-top: 1.389vw;

    ${media.fullWidth} {
      margin-top: 20px;
    }

    ${media.tablet} {
      margin-top: 1.953vw;
    }

    ${media.mobile} {
      ${text.h4};
      margin-top: 4.673vw;
    }
  }

  p {
    ${text.bodyMd};
  }
  li {
    ${text.bodyMd};
    margin-top: 1.042vw;

    ${media.fullWidth} {
      margin-top: 15px;
    }

    ${media.tablet} {
      margin-top: 1.465vw;
    }

    ${media.mobile} {
      margin-top: 3.505vw;
    }
  }

  ul,
  ol {
    margin-left: 3.472vw;
    margin-top: 1.042vw;

    ${media.fullWidth} {
      margin-left: 50px;
      margin-top: 15px;
    }

    ${media.tablet} {
      margin-left: 4.883vw;
      margin-top: 1.465vw;
    }

    ${media.mobile} {
      margin-left: 11.682vw;
      margin-top: 3.505vw;
    }
  }

  ${media.fullWidth} {
    padding: 20px 0px;
  }

  ${media.tablet} {
    padding: 1.953vw 0vw;
  }

  ${media.mobile} {
    padding: 4.673vw 0vw;
  }
`

const ContentDiv = styled.div``

const BottomCopy = styled(IntroBody)``

const BodyCopy = styled(IntroBody)`
  padding: unset;
  margin: unset;
`

const SectionId = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.lightPurple};
  color: ${colors.white};
  width: 6.944vw;
  height: 6.944vw;
  border-radius: 0.556vw;

  ${media.fullWidth} {
    width: 100px;
    height: 100px;
    border-radius: 8px;
  }

  ${media.tablet} {
    display: none;
  }

  ${media.mobile} {
    display: none;
  }
`

const Header = styled.h4`
  ${text.h4}
  color:${props =>
    props.$color ? `${colors[props.$color]}` : `${colors.black}`};
`

const CheckmarkIcon = styled.img`
  width: 2.5vw;
  height: 2.5vw;
  ${media.fullWidth} {
    width: 36px;
    height: 36px;
  }

  ${media.tablet} {
    width: 3.516vw;
    height: 3.516vw;
  }

  ${media.mobile} {
    width: 8.411vw;
    height: 8.411vw;
  }
`

const Icon = styled.img`
  width: 5vw;
  height: 5vw;

  ${media.fullWidth} {
    width: 72px;
    height: 72px;
  }

  ${media.tablet} {
    width: 7.031vw;
    height: 7.031vw;
  }

  ${media.mobile} {
    width: 16.822vw;
    height: 16.822vw;
  }
`

const HeaderDiv = styled.div`
  ${text.h3}
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 1.389vw 0vw 0vw 0vw;

  ${media.fullWidth} {
    gap: 24px;
    padding: 20px 0px 0px 0px;
  }

  ${media.tablet} {
    gap: unset;
    padding: 1.953vw 0vw 0vw 0vw;
  }

  ${media.mobile} {
    gap: unset;
    padding: 4.673vw 0vw 0vw 0vw;
  }
`

const Wrapper = styled.div`
  width: 79.444vw;
  height: auto;
  border-top: ${props =>
    props.$lineseperator ? `0.139vw solid ${colors.grey400}` : "unset"};
  padding-top: ${props => (props.$lineseperator ? "20px" : "unset")};

  ${media.fullWidth} {
    width: 1144px;
    border-top: ${props =>
      props.$lineseperator ? `2px solid ${colors.grey400}` : "unset"};
    padding-top: ${props => (props.$lineseperator ? "20px" : "unset")};
  }

  ${media.tablet} {
    width: 92.09vw;
    border-top: ${props =>
      props.$lineseperator ? `0.195vw solid ${colors.grey400}` : "unset"};
    padding-top: ${props => (props.$lineseperator ? "7.813vw" : "unset")};
  }

  ${media.mobile} {
    width: 88.551vw;
    border-top: ${props =>
      props.$lineseperator ? `0.467vw solid ${colors.grey400}` : "unset"};
    padding-top: ${props => (props.$lineseperator ? "20px" : "unset")};
  }
`
